/*
 *Character Limits
 *
 */
export const SIXTY_CHARACTERS_MAXIMUM = 60;
export const SEVEN_CHARACTERS_MAXIMUM = 7;
export const FOUR_CHARACTERS_MINIMUM = 4;

/**
 * Non especial characters Regex. But allows spaces and ampersands, exclamation and question marks.
 *
 * @example
 * abc
 * 123
 * abc 123
 * abc&123
 * abc 123 abc
 *
 */

export const NON_SPECIAL_CHARACTERS_REGEX = /^[a-zA-Z0-9\s&!?-]+$/;

/**
 * System user password.
 *
 * @example
 * 1234567890aA__
 *
 * (?=.*[a-z])   // see if at least one lower case letter exists
 * (?=.*[A-Z])   // see if at least one upper case letter exists
 * (?=.*\d)      // see if at least one digit exists
 * (?=.*\W])     // see if at least one non-word character exists
 */
export const PASSWORD_VALIDATION_REGEX =
  /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[\W|_])/;

/**
 * UUID Regex.
 *
 * @example
 * 123e4567-e89b-12d3-a456-426655440000
 */
export const UUID_REGEX =
  /^[0-9a-fA-F]{8}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{4}\b-[0-9a-fA-F]{12}$/;

/**
 * ISO 8601 date string Regex.
 *
 * @example
 * 2023-05-10T08:47:01.099000+00:00
 */
export const ISO_8601_DATE_STR =
  /^([+-]?\d{4}(?!\d{2}\b))((-?)((0[1-9]|1[0-2])(\3([12]\d|0[1-9]|3[01]))?|W([0-4]\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\d|[12]\d{2}|3([0-5]\d|6[1-6])))([T\s]((([01]\d|2[0-3])((:?)[0-5]\d)?|24:?00)([.,]\d+(?!:))?)?(\17[0-5]\d([.,]\d+)?)?([zZ]|([+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?)?$/;

/**
 * PEM certificate Regex.
 *
 * @example
 * -----BEGIN (CERTIFICATE | CERTIFICATE REQUEST)-----
 * MIIFaDCCBFCgAwIBAgISESHkvZFwK9Qz0KsXD3x8p44aMA0GCSqGSIb3DQEBCwUA
 * VQQDDBcqLmF3cy10ZXN0LnByb2dyZXNzLmNvbTCCASIwDQYJKoZIhvcNAQEBBQAD
 * ggEPADCCAQoCggEBAMGPTyynn77hqcYnjWsMwOZDzdhVFY93s2OJntMbuKTHn39B
 * ...
 * bml6YXRpb252YWxzaGEyZzIuY3JsMIGgBggrBgEFBQcBAQSBkzCBkDBNBggrBgEF
 * BQcwAoZBaHR0cDovL3NlY3VyZS5nbG9iYWxzaWduLmNvbS9jYWNlcnQvZ3Nvcmdh
 * bml6YXRpb252YWxzaGEyZzJyMS5jcnQwPwYIKwYBBQUHMAGGM2h0dHA6Ly9vY3Nw
 * lffygD5IymCSuuDim4qB/9bh7oi37heJ4ObpBIzroPUOthbG4gv/5blW3Dc=
 * -----END (CERTIFICATE | CERTIFICATE REQUEST)-----
 */
export const CERT_VALIDATION_REGEX =
  /^(?:(?!-{3,}(?:BEGIN|END) (CERTIFICATE REQUEST|CERTIFICATE))[\s\S])*(-{3,}BEGIN (CERTIFICATE REQUEST|CERTIFICATE)(?:(?!-{3,}END (CERTIFICATE REQUEST|CERTIFICATE))[\s\S])*?-{3,}END (CERTIFICATE REQUEST|CERTIFICATE)-{3,})(?![\s\S]*?-{3,}BEGIN (CERTIFICATE REQUEST|CERTIFICATE)[\s\S]+?-{3,}END (CERTIFICATE REQUEST|CERTIFICATE)[\s\S]*?$)/;

/**
 * Phone number Regex.
 *
 * @example
 * // Regular local phone number (XXX) XXX-XXXX
 * 1231231231
 * 123-1231231
 * 123123-1231
 * 123-123 1231
 * 123 123-1231
 * 123-123-1231
 * (123)123-1231
 * (123)123 1231
 * (123) 123-1231
 * (123) 123 1231
 *
 * // International codes +XXX (XXX) XXX-XXXX
 * +99 1234567890
 * +991234567890
 *
 * // Extensions (XXX) XXX-XXXX Ext. XXX...
 * 1234567890 Ext 1123123
 * 1234567890 Ext: 1123123
 * 1234567890 Ext. 1123123
 * 1234567890 ext 1123123
 * 1234567890 ext: 1123123
 * 1234567890 X 1123123
 */
export const PHONE_NUMBER_VALIDATION_REGEX =
  /(\+\d{1,3}\s?)?((\(\d{3}\)\s?)|(\d{3})(\s|-?))(\d{3}(\s|-?))(\d{4})(\s?(((ext|extension)[:|.|]?)|x)(\s?\d+))?/i;

/**
 * Mobile country code (MCC) length.
 */
export const MCC_LEN = 3;

/**
 * Mobile country code (MCC).
 *
 * @example
 * 289
 * 412
 * 603
 */
export const MCC_VALIDATION_REGEX = new RegExp(`^\\d{${MCC_LEN}}$`);

/**
 * Mobile country code (MCC) length.
 */
export const MNC_MAX_LEN = 3;

/**
 * Mobile network code (MNC).
 *
 * @example
 * 88
 * 68
 * 840
 */
export const MNC_VALIDATION_REGEX = new RegExp(`^\\d{2,${MNC_MAX_LEN}}$`);

/**
 * Cognito password reset code length.
 */
export const PWD_RESET_CODE_LEN = 6;

/**
 * Cognito password reset code.
 *
 * @example
 * 111111
 * 123456
 * 823465
 */
export const PWD_RESET_CODE_REGEX = new RegExp(`^\\d{${PWD_RESET_CODE_LEN}}$`);

/**
 * MFA code length.
 */
export const MFA_CODE_LEN = 6;

/**
 * MFA code.
 *
 * @example
 * 111111
 * 123456
 * 823465
 */
export const MFA_CODE_REGEX = new RegExp(`^\\d{${MFA_CODE_LEN}}$`);

/**
 * URL.
 *
 * @example
 * https://www.google.com
 * http://www.google.com
 * www.google.com
 * google.com
 */
export const URL_REGEX =
  /^(https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;

/**
 * Blob URL created by URL.createObjectURL().
 *
 * @example
 * blob:http://localhost:3000/550e8400-e29b-41d4-a716-446655440000
 * blob:https://example.com/550e8400-e29b-41d4-a716-446655440000
 */
export const BLOB_URL_REGEX =
  /^blob:(https?:\/\/[a-zA-Z0-9.-]+(:[0-9]+)?(\/[a-zA-Z0-9-]+)+)$/;

/**
 * API URL.
 *
 * @example
 * https://ues.test.netlync-nonprod.com
 * https://es-nl.massresponse.com/livemvnowebservice/rest/nles/
 */
export const API_URL_REGEX =
  /^https?:\/\/[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)$/;

/**
 * API URL.
 *
 * @example
 * https://ues.test.netlync-nonprod.com
 * https://es-nl.massresponse.com/livemvnowebservice/rest/nles/
 */
export const SMDP_REGEX =
  /^[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&=]*)$/;

/**
 * LOGO URL.
 *
 * @example
 * https://static.wixstatic.com/media/2df569_7912ef2312a546ffb1ecb028ed560ed7~mv2.png/v1/fill/w_326,h_124,al_c,q_85,usm_0.66_1.00_0.01,enc_auto/2df569_7912ef2312a546ffb1ecb028ed560ed7~mv2.png
 *
 */
export const LOGO_URL = /^(https?:\/\/.*\.(?:png|jpg))$/;

/**
 * IMSI max length.
 */
export const IMSI_MAX_LEN = 15;

/**
 *
 * @example
 * ICCID max length.
 *
 */
export const ICCID_MAX_LEN = 21;

/**
 * IMSI code Regex.
 *
 * @example
 * 310170845466094
 * 502130123456789
 * 460001357924680
 */
export const IMSI_REGEX = /^[0-9][0-9]{14,15}$/;

/**
 * EID code Regex.
 *
 * @example
 * 23423432432432432432423423423423
 */
export const EID_REGEX = /^[0-9]{32}$/;

/**
 * ICCID code Regex.
 *
 * @example
 * 8927071991486189350F
 * 89270719914861893520F
 * 8927071991486189312
 */
export const ICCID_REGEX = /^\d{19,20}[Ff]?$/;

/**
 * IMEI code Regex.
 *
 * @example
 * 12345678912345
 */
export const IMEI_REGEX = /[0-9]{14,15}$/;

/**
 * Non digits Regex.
 * Matches every character that is not a digit.
 *
 * @example
 * abc
 * !*&^%$#@
 */
export const NON_DIGITS_REGEX = /[^0-9]/;

/**
 * Non digits Regex.
 * Matches every character that is not a digit or an F.
 *
 * @example
 * abc
 * !*&^%$#@
 */
export const NON_DIGITS_ICCID_REGEX = /[^0-9F]/i;

/**
 * Non phone number digits Regex.
 * Matches every character that is not a valid phone character
 *
 * @example
 * abc
 * !*&^%$@
 */
export const NON_PHONE_DIGITS_REGEX = /[^\d-()+\s]/g;

/**
 * Only digits Regex.
 *
 * @example
 * 1234567890
 */
export const ONLY_DIGITS_REGEX = /^\d+$/;

/**
 * Only digits Regex.
 *
 * @example
 * Hello World
 * Hola Mundo
 */
export const ONLY_LETTERS_AND_SPACES = /^[a-zA-Z\s]+$/;

/**
 * Only digits and commas Regex.
 *
 * @example
 * 123490,1987
 */
export const ONLY_DIGITS_AND_COMMA_REGEX = /^([0-9,])+$/;

/**
 * Ranges
 * Matches with a range of digits from 1 to 15 length.
 *
 * @example
 * 2400755631799940 - 2400755631799945
 */

export const IMSI_RANGE_REGEX = /^([\d]{1,15})([\D]+)([\d]{1,15})$/;

/**
 * No Special Characters
 *
 * @example
 * abced-213232
 * 43423424
 * dkjfkdsjfdksf
 * space space
 */

export const NO_SPECIAL_CHARACTERS = /[^a-zA-Z0-9-\s.extnsio+]/;

/**
 * Challenge code Regex.
 *
 * @example
 * AdcARBcBAAABBQAAFddxOHyQFtX9RBCSCSY02wIFAABL4jLogn6AAFY/rP2VByQQCwUAAKBSB5rZk89ECqRrW1JkPf8=
 */
export const CHALLENGE_REGEX = /[^a-zA-Z0-9-\s.extnsio+]/;

/**
 * Challenge Response code Regex.
 *
 * @example
 * AtcAKBcBAAADAwBAv5y/WqYAGXILBQAA2+qSNtkWYt6Mjk2kGN3Rdw==
 */
export const CHALLENGE_RESPONSE_REGEX = /[^a-zA-Z0-9-\s.extnsio+]/;

/**
 * SUBSCRIBER_ID code Regex.
 *
 * @example
 * 0313460000000001@nai.epc.mnc007.mcc240.3gppnetwork.org
 *
 * not allowed
 * 0313460000000001@nai.$epc.mnc007.mcc240.3gppnetwork.org
 * 121*Woho&
 */
export const SUBSCRIBER_ID = /^([A-Za-z0-9\\@.])+$/;

/**
 * NAME_INPUTS
 *
 * @example
 * allowed
 * Abá
 * Jérémie O'Co-nor
 * Worthington-Smythe
 *
 * not allowed
 * £john
 * *Woho&
 */

export const NAME_INPUTS = /^([ \u00c0-\u01ffa-zA-Z'\\-])+$/;

/**
 * STRING_NUMBERS_DASH_AND_UNDERSCORES
 *
 * @example
 * allowed
 * 1234ABCD_abcd-3456
 * 1234_eerew-GFFF
 *
 * not allowed
 * £john
 * *Woho&
 */
export const STRING_NUMBERS_DASH_AND_UNDERSCORES = /^([A-Za-z0-9\-\\_])+$/;

/**
 * ADDRESS_VALIDATION
 *
 * @example
 * allowed
 * #3 the square
 * D'olier Street,
 * Rich & Richards
 *
 * not allowed
 * st_marys street
 * *SilviaLane
 * $£24
 */
export const ADDRESS_VALIDATION = /[a-zA-Z0-9\s\-#.,&'()]+$/;

/**
 * SLUG_INPUT
 *
 * @example
 * allowed
 * lowercasestuffonly
 * test-here-something
 * number22
 * netlync-123
 * this-1232_is-allowed
 *
 * not allowed
 * this space
 * CapitalWords
 */

export const SLUG_INPUT = /^([\u00c0-\u01ffa-z0-9'\-\\_])+$/;

/**
 * A regular expression to validate phone numbers.
 *
 * This regex allows:
 * - Digits (0-9)
 * - Optional "+" for international numbers
 * - Spaces, dashes ("-"), and parentheses ("()")
 * - A length between 7 and 15 characters
 *
 * Examples of valid phone numbers:
 * - "123456789"
 * - "+123 456 789"
 * - "(123) 456-7890"
 * - "+1 (123) 456-7890"
 *
 * Examples of invalid phone numbers:
 * - "12345" (too short)
 * - "123-abc-456" (letters are not allowed)
 * - "+1234567890123456" (too long)
 *
 * @constant {RegExp} PHONE_NUMBER_REGEX
 */
export const PHONE_NUMBER_REGEX = /^\+?[1-9]\d{1,14}$/;

/**
 * Fully Qualified Domain Name (FQDN) validator.
 *
 * @example
 * www.google.com
 * sub.example.co.uk
 * my-server123.domain.io
 *
 * ^(?=.{1,253}$)       // Ensures the total length is between 1 and 253 characters
 * (?!-)                // Prevents starting with a hyphen
 * [A-Za-z0-9-]{1,63}   // Matches subdomains (1-63 chars: letters, numbers, or hyphens)
 * (?:\.[A-Za-z0-9-]{1,63})+  // Allows multiple subdomains separated by dots
 * \.[A-Za-z]{2,}       // Ensures a valid top-level domain (TLD) with at least 2 letters
 */

export const FQND_REGEX =
  /^(?=.{1,253}$)(?!-)[A-Za-z0-9-]{1,63}(?:\.[A-Za-z0-9-]{1,63})+\.[A-Za-z]{2,}$/;
